<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useVirtualKeyboard } from '@/composables/keyboard'
import { $bus } from '@/common/events'

//Note: this is an upgraded TextBox with custom caret for touch screens and virtual keyboard

const { vkeyboard, keyboardType, canRepositionCaret, activeTarget } = useVirtualKeyboard();

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: ""
  },
  enabled: {
    type: Boolean,
    default: true
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  masked: {
    type: Boolean,
    default: false
  },
  grid: {
    type: Boolean,
    default: false
  },
  validation: {
    type: String,
    default: null
  },
  modelValue: {
  }
});

const emit = defineEmits([ "update:modelValue", "change", "enter" ]);

const focus = (e) => { };
const unfocus = (e) => { };
defineExpose({ focus, unfocus });

const type = computed(() => props.masked == true ? "password" : "text");

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    emit('change', value);
  }
});

const textbox = ref(null);
const customCaret = ref();
const textBefore = ref();
const textAfter = ref();

const isFocusForKeyboard = computed(() => activeTarget.value === textbox.value);

watch(isFocusForKeyboard, (focused) => {
  if (focused)
    vkeyboard.on("enter", onEnterKey);
  else
    vkeyboard.removeListener("enter", onEnterKey);
  updateCaretPosition();
});

onMounted(() => {
  initializeTextMirror();
  updateCaretPosition();
});

onUnmounted(() => {
  vkeyboard.removeListener("enter", onEnterKey);
  disposeTextMirror();
});

function updateCaretPosition() {
  const input = textbox.value;
  const fullText = input.value;

  textBefore.value.textContent = fullText;
  textAfter.value.textContent = "";

  const splitAt = (isDraggingCaret.value && currentDragIdx != null) ? currentDragIdx : input.selectionStart;

  if (customCaret.value) {
    if (!isFocusForKeyboard.value) {
      customCaret.value.style.display = 'none';
      caretTeardrop.value = { visible: false, left: 0, top: 0 };
      return;
    }
    customCaret.value.style.display = '';
    const paddingLeft = parseFloat(window.getComputedStyle(input).paddingLeft) || 0;
    const charW = measureCharWidth();
    const caretX = paddingLeft + splitAt * charW;
    const caretWidth = parseFloat(window.getComputedStyle(customCaret.value).width) || 4;
    customCaret.value.style.left = caretX + 'px';

    const inputRect = input.getBoundingClientRect();
    const container = input.parentElement?.getBoundingClientRect();
    caretTeardrop.value = {
      visible: true,
      left: caretX + caretWidth / 2,
      top: container ? (inputRect.bottom - container.top - 2) : (inputRect.height - 2),
    };
  }
}

function selectionChange() {
  //Only track selection changes if our input is currently active
  //if (document.activeElement === textbox.value) {
    updateCaretPosition();
  //}
}

function initializeTextMirror() {
  //Update when typing or deleting text
  textbox.value?.addEventListener('input', updateCaretPosition);

  //Update when tapping, moving the cursor, or selecting text on mobile
  document.addEventListener('selectionchange', selectionChange);
}

function disposeTextMirror() {
  textbox.value?.removeEventListener('input', updateCaretPosition);
  document.removeEventListener('selectionchange', selectionChange);
}

const isDraggingCaret = ref(false);
const magnifier = ref({ visible: false, left: 0, top: 0, text: "" });
const caretTeardrop = ref({ visible: false, left: 0, top: 0 });
let cachedCharWidth = 0;
let dragStartIdx = 0;
let currentDragIdx = null;

function measureCharWidth() {
  if (cachedCharWidth > 0) return cachedCharWidth;
  const el = textbox.value;
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  cachedCharWidth = ctx.measureText("M").width;
  return cachedCharWidth;
}

function charIndexAt(clientX) {
  const el = textbox.value;
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const localX = clientX - rect.left - paddingLeft;
  const charW = measureCharWidth();
  if (charW <= 0) return 0;
  const idx = Math.round(localX / charW);
  const value = el.value || "";
  return Math.max(0, Math.min(value.length, idx));
}

function updateCaretFromPoint(clientX, clientY) {
  const el = textbox.value;
  if (!el) return;
  const idx = charIndexAt(clientX);
  currentDragIdx = idx;
  const selStart = Math.min(dragStartIdx, idx);
  const selEnd = Math.max(dragStartIdx, idx);
  el.selectionStart = selStart;
  el.selectionEnd = selEnd;
  updateCaretPosition();
  const val = el.value || "";
  const from = Math.max(0, idx - 2);
  const to = Math.min(val.length, idx + 3);
  const snippet = val.substring(from, to);
  const container = el.parentElement?.getBoundingClientRect();
  const cx = container ? clientX - container.left : clientX;
  const cy = container ? clientY - container.top : clientY;
  magnifier.value = {
    visible: true,
    left: cx,
    top: cy - 56,
    text: snippet || " ",
  };
}

function onPointerDown(e) {
  if (!props.enabled || props.readonly) return;
  if (e.pointerType === "mouse" && e.button !== 0) return;
  e.preventDefault();
  e.target.focus({ preventScroll: true });
  dragStartIdx = charIndexAt(e.clientX);
  isDraggingCaret.value = true;
  try {
    e.target.setPointerCapture(e.pointerId);
  }
  catch {}
  updateCaretFromPoint(e.clientX, e.clientY);
}

function onPointerMove(e) {
  if (!isDraggingCaret.value) return;
  updateCaretFromPoint(e.clientX, e.clientY);
}

function onPointerEnd(e) {
  if (!isDraggingCaret.value) return;
  isDraggingCaret.value = false;
  currentDragIdx = null;
  magnifier.value = { visible: false, left: 0, top: 0, text: "" };
  updateCaretPosition();
  try {
    e.target.releasePointerCapture(e.pointerId);
  }
  catch {}
}

function onFocus(e) {
  if (props.enabled && !props.readonly) {
    keyboardType.value = props.type == "numeric" ? "numeric" : "default";
    focus(e);
    vkeyboard.show(e.target);
  }
}

function onUnfocus(e) {
  if (props.enabled && !props.readonly) {
    unfocus(e);
    vkeyboard.hide(e.target, { delay: 120 }); //Give other input a chance to cancel hide within 30ms
  }
}

function onEnterKey() {
  emit("enter");
}
</script>

<template>
  <div class="input-container">

    <!-- TODO: Test password, test autocomplete (prefill form), test more than one instance -->
    <input :type="type" :id="props.id" ref="textbox" v-model="value" :placeholder="props.placeholder"
      :required="!!props.required" :readonly="!!props.readonly" :disabled="!props.enabled"
      role="presentation" autocomplete="new-password"
      class="custom-input rounded-lg block" :class="[ { 'textbox-error': !!props.validation, 'focus-for-keyboard': isFocusForKeyboard }, $attrs.class ]"
      @focus="onFocus" @blur="onUnfocus" @keypress.enter="onEnterKey"
      @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerEnd" @pointercancel="onPointerEnd" />
    
    <!-- The mirror layer now handles split text and the caret -->
    <div class="text-mirror truncate" :class="[ { 'textbox': !props.grid, 'textbox-grid': !!props.grid, 'textbox-error': !!props.validation, 'focus-for-keyboard': isFocusForKeyboard }, $attrs.class ]">
      <span class="text-before" ref="textBefore"></span>
      <span class="custom-caret" ref="customCaret"></span>
      <span class="text-after" ref="textAfter"></span>
    </div>

    <!-- Floating teardrop -->
    <div v-if="caretTeardrop.visible" class="caret-teardrop"
      :style="{ left: caretTeardrop.left + 'px', top: caretTeardrop.top + 'px' }"></div>

    <!-- Magnifier bubble -->
    <div v-if="magnifier.visible" class="caret-magnifier"
      :style="{ left: magnifier.left + 'px', top: magnifier.top + 'px' }">
      {{ magnifier.text }}
    </div>

    <!-- Validation -->
    <div class="textbox-validation-message" :class="$attrs.class" v-if="!!props.validation">
      {{ typeof validation == "string" ? validation : validation.message }}
    </div>

  </div>
</template>

<style scoped>
.textbox {
  @apply bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
    focus:ring-blue-500 focus:border-blue-500
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

.textbox-grid {
  @apply bg-white border-l border-r border-gray-300 text-gray-900 text-sm block p-2.5
    focus:ring-blue-500 focus:border-blue-500
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

.textbox:disabled,
.textbox-grid:disabled {
  @apply text-gray-400;
}

.textbox-error {
  @apply border-red-700 bg-red-100 text-red-900 focus:ring-red-500 focus:border-red-500;
}

.items-group .textbox {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0;
}

input[type="text"].textbox:focus,
input[type="text"].textbox.focus-for-keyboard,
.text-mirror.textbox.focus-for-keyboard,
.text-mirror.textbox-grid.focus-for-keyboard {
  @apply ring-blue-500 border-blue-500;

  outline: 2px solid transparent;
  outline-offset: 2px;
  --tw-ring-inset: var(--tw-empty, );
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: #1C64F2;
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
  border-color: #1C64F2;
}

:root {
  --caret-width: 4px; /* Thicker for touch screens */
  --caret-color: #007bff;
}

.input-container {
  position: relative;
  display: inline-block;
  width: 100%;
  /* max-width: 300px; */
}

/* The actual input field is transparent but captures text/focus */
.custom-input {
  @apply text-sm p-2.5;
  width: 100%;
  /* padding: 12px; */ /* Large tap target for fingers */
  /* font-size: 18px; */ /* Prevents iOS auto-zoom */
  font-family: monospace; /* Monospace makes tracking math easier */
  /* border: 2px solid #ccc; */
  /* border-radius: 8px; */
  outline: none;
  background: transparent;
  color: transparent; /* Hide text so we can style it underneath */
  caret-color: transparent; /* Hide native cursor */
  position: relative;
  z-index: 2;
}

/* Rendered text layer underneath */
.text-mirror {
  @apply text-sm p-2.5;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  /* padding: 12px; */
  /* font-size: 18px; */
  font-family: monospace;
  color: #333;
  white-space: pre;
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: 1.5em;
}

.custom-caret {
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: var(--caret-width, 4px);
  height: 1.4em;
  background-color: var(--caret-color, #007bff);
}

/* Floating teardrop under the caret */
.caret-teardrop {
  position: absolute;
  z-index: 100;
  width: 16px;
  height: 16px;
  background-color: var(--caret-color, #007bff);
  border-radius: 0 50% 50% 50%;
  transform: translateX(-50%) rotate(45deg);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

/* Only blink when the actual input has focus */
.custom-input.focus-for-keyboard ~ .text-mirror .custom-caret,
.custom-input:focus ~ .text-mirror .custom-caret {
  display: block;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  from, to { background-color: var(--caret-color, #007bff) }
  50% { background-color: var(--caret-color-dim, #7dd3fc) }
}

/* Magnifier bubble */
.caret-magnifier {
  @apply bg-white text-gray-900 dark:bg-gray-800 dark:text-white;
  position: absolute;
  z-index: 100;
  transform: translateX(-50%);
  padding: 6px 12px;
  min-width: 44px;
  text-align: center;
  border: 2px solid var(--caret-color, #007bff);
  border-radius: 0px;
  font-family: monospace;
  font-size: 22px;
  line-height: 1;
  white-space: pre;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  pointer-events: none;
}
.caret-magnifier::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -8px;
  transform: translateX(-50%);
  border: 8px solid transparent;
  border-top-color: var(--caret-color, #007bff);
  border-bottom: 0;
}
</style>